<template>
  <div class="">
    <div v-if="hasRole('System Administrator')" class="text-sm text-grey  mx-5">
      <a href="/assignment-admin">Assignments</a> > {{ assignment.assignmentName }}
    </div>
    <div v-else>
      <a href="/assignments">Assignments</a> > {{ assignment.assignmentName }}
    </div>
    <div class="pt-5">
      <div class="flex justify-between items-center">
        <div class="h2 text-primary-500 mx-5">
          {{ assignment.assignmentName }}
        </div>
        <div>
          <div class="flex flex-row justify-end sp-x-1">
            <div v-if="hasRole('System Administrator')" class="flex cursor-pointer">
              <d-tooltip label="View All Assignees">
                <d-icon v-if="assignment.assignmentTemplateGroupId != null" class="text-3xl" icon="users" @click="launchGroupAssigneesSideDrawer" />
                <d-icon v-else class="text-3xl" icon="users" @click="launchIndividualAssigneesSideDrawer" />
              </d-tooltip>
            </div>

            <d-tooltip label="Feedback">
              <div class="cursor-pointer mr-2 ml-5" @click="launchAssignmentFeedbackSideDrawer()">
                <span>({{ assignmentFeedback.length }})</span>
              </div>
              <div class="flex cursor-pointer" @click="launchAssignmentFeedbackSideDrawer()">
                <d-icon class="text-3xl" icon="comments" />
              </div>
            </d-tooltip>
          </div>
        </div>
      </div>
      <div class="mb-5 mx-5">
        <div class="text-black">
          Due Date: {{ assignment.assignmentDueDate | formatShortDate }}
        </div>
        <div v-if="assignment.userId !== profile.id" class="text-black">
          Assigned User: {{ assignment.userFullName }}
        </div>
        <div class="text-black" v-if="assignment.reviewerFullName">
          Reviewer: {{ assignment.reviewerFullName}}
        </div>
        <p v-if="assignment.optional" class="text-gray-700 text-xs">
          This assignment is optional
        </p>
      </div>
      <d-message v-if="assignment && assignment.submitted && !assignment.completed" type="info">
        <p class="font-bold">
          Assignment has been submitted for review
        </p>
        <p v-if="user.id == assignment.userId">
          This assignment is still pending for review at this time. You will receive feedback if any further updates are needed.
        </p>
        <p v-else>
          This assignment has been submitted for review. You can complete it by clicking the "Mark as Completed" button below (if you are the reviewer), otherwise leave feedback for the assignee.
        </p>
      </d-message>
      <d-message v-if="assignment && assignment.completed" type="info">
        <p class="font-bold">
          Assignment Completed
        </p>
        <p>
          This assignment has been fully completed and can no longer be modified.
        </p>
      </d-message>

      <div class="flex">
        <div class="w-8/12 mx-5">
          <div class="mb-5">
            <div class="flex flex-row cursor-pointer" @click="expandedGuidelines = !expandedGuidelines">
              <d-icon icon="chevron-up" class="expand-icon cursor-pointer  px-3 transform" :class="{'rotate-180': expandedGuidelines}" />
              <div class="font-bold text-lg py-5 select-none">
                Guidelines
              </div>
            </div>
            <transition>
              <div v-if="expandedGuidelines" class="px-6 py-4 bg-white  rounded-md shadow-sm">
                <div class="wysiwyg-editor-container" v-html="assignment.assignmentHtmlContent" />
              </div>
            </transition>
          </div>
        </div>

        <div class="md:w-4/12 mx-5">
          <div>
            <div class="cursor-pointer" @click="expandToDos = !expandToDos">
              <div class="flex flex-row">
                <d-icon icon="chevron-up" class="expand-icon cursor-pointer  px-3 transform" :class="{'rotate-180': expandToDos}" />
                <div class="select-none font-bold text-lg py-5">
                  Tasks
                </div>
              </div>
            </div>
            <transition>
              <div v-if="expandToDos">
                <div v-if="loadingToDos" class="py-10 flex justify-center">
                  <d-loader />
                </div>
                <div v-else>
                  <ul v-for="todo in assignmentToDos" :key="todo.id" class="width-full list-none">
                    <li class="border-gray-300 border-solid mb-2 cursor-pointer">
                      <a :href="(todo.toDoItemPreRequisiteComplete || todo.submitted || hasRole('System Administrator')) ? '/assignments/todo/' + todo.id : 'javascript:;'" class="flex no-underline" :class="[todo.toDoItemPreRequisiteComplete || hasRole('System Administrator')? 'cursor-pointer' : 'cursor-default' ]">
                        <div class="bg-primary-100 text-primary-500 select-none rounded-md flex flex-1 justify-between items-center p-4 shadow-sm">
                          <div>
                            <div class=" text-xl font-bold">{{ todo.toDoItemName }}</div>
                            <div class="text-black ">
                              {{ todo.toDoItemDueDate | formatShortDate }}
                            </div>
                          </div>
                          <div class="rounded-md w-10 h-10 mr-4 flex justify-center items-center">
                            <d-tooltip v-if="!todo.toDoItemPreRequisiteComplete" :label="`You must complete &quot;${todo.toDoItemPreRequisiteName}\&quot; to continue`">
                              <d-icon class="" :class="{'text-green-500' : todo.completed}" :icon="toDoStatusIcon(todo)" />
                            </d-tooltip>
                            <d-icon v-else class="" :class="{'text-green-500' : todo.completed}" :icon="toDoStatusIcon(todo)" />
                          </div>
                        </div>

                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div v-if="assignment" class="flex justify-end pt-5">
        <div v-if="!assignment.completed && !loadingToDos">
          <div v-if="userCanSubmit">
            <d-tooltip v-if="!assignmentCanBeSubmitted" label="You must complete all requirements before you can submit your assignment.">
              <d-btn disabled>
                Submit For Review
              </d-btn>
            </d-tooltip>
            <d-btn v-else @click="submitAssignmentForReview">
              Submit For Review
            </d-btn>
          </div>
          <div v-if="userCanComplete">
            <d-btn @click="markAssignmentCompleted">
              Mark as Completed
            </d-btn>
          </div>
        </div>
        <div v-if="userCanUncomplete">
          <d-btn @click="markAssignmentUncompleted">
            Mark as Not Completed
          </d-btn>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import FeedbackSideDrawer from './components/FeedbackSideDrawer';
  import IndividualAssigneeSideDrawer from '../AdminAssignments/components/IndividualAssigneeSideDrawer';
  import GroupAssigneesSideDrawer from '../AdminAssignments/components/GroupAssigneesSideDrawer';

  export default {
    parent: true,

    props: {
      user: {
        type: Object,
        default: () => { },
      },
      assignmentId: {
        type: String,
        default: () => '',
      },
    },

    data: () => ({
      assignment: {},
      assignmentFeedback: [],
      assignmentToDos: [],
      expandToDos: true,
      expandedGuidelines: true,
      loadingAssignment: false,
      loadingToDos: false,
      assignmentForm: {
        completed: false,
        submitted: false,
      },
    }),

    computed: {
      ...mapGetters('d2Auth', ['profile', 'hasRole']),

      assignmentCanBeSubmitted() {
        return this.assignment.completedToDosRatio.completed === this.assignment.completedToDosRatio.outOfTotal;
      },
      userCanSubmit() {
        if (!this.assignmentCanBeSubmitted || this.assignment.submitted) {
          return false;
        }

        if (this.hasRole('System Administrator')) {
          return true;
        }

        if (this.profile.id === this.assignment.userId) {
          return true;
        }

        return false;
      },
      userCanComplete() {
        if (!this.assignmentCanBeSubmitted || !this.assignment.submitted) {
          return false;
        }

        if (this.hasRole('System Administrator')) {
          return true;
        }

        if (this.profile.id === this.assignment.reviewerId && this.assignment.completableByReviewerOnly) {
          return true;
        }

        return false;
      },
      userCanUncomplete() {
        if (!this.assignment.completed) {
          return false;
        }

        if (this.hasRole('System Administrator')) {
          return true;
        }

        return false;
      },
    },

    mounted() {
      this.getAssignmentById();
      this.getAssignmentFeedback();
      this.getAssignmentToDos();
    },

    methods: {
      async getAssignmentById() {
        this.loadingAssignment = true;
        try {
          const { data } = await axios.get(
            `/api/user/assignments/${this.assignmentId}`,
          );
          this.assignment = data;
          this.loadingAssignment = false;
        } catch (error) {
          this.$toast.open({
            message: error.message,
            color: 'red-500',
            duration: 4000,
          });
          this.loadingAssignment = false;
        }
      },

      async getAssignmentFeedback() {
        this.loadingAssignment = true;
        try {
          const { data } = await axios.get(
            `/api/assignments/${this.assignmentId}/feedback`,
          );
          this.assignmentFeedback = data;
          this.loadingAssignment = false;
        } catch (error) {
          this.$toast.open({
            message: error.message,
            color: 'red-500',
            duration: 4000,
          });
          this.loadingAssignment = false;
        }
      },

      async getAssignmentToDos() {
        this.loadingAssignment = true;
        try {
          const { data } = await axios.get(
            `/api/user/assignment/to-dos/paged?currentPage=1&pageSize=100&userAssignmentId=${this.assignmentId}`,
          );
          this.assignmentToDos = data.resultItems;
        } catch (error) {
          this.$toast.error(error.response.data);
        } finally {
          this.loadingToDos = false;
        }
      },

      async submitAssignmentForReview() {
        this.assignmentForm.completed = false;
        this.assignmentForm.submitted = true;
        try {
          await axios.patch(
            `/api/user/assignments/${this.assignment.id}`,
            this.assignmentForm,
          );
          this.getAssignmentById();
          this.getAssignmentToDos();
          this.$toast.open({
            message: `${this.assignment.assignmentName} submitted!`,
            color: 'green-500',
          });
        } catch (error) {
          this.$toast.open({ message: error.message, color: 'red-500' });
        }
      },

      async markAssignmentCompleted() {
        this.assignmentForm.completed = true;
        this.assignmentForm.submitted = true;
        try {
          await axios.patch(
            `/api/user/assignments/${this.assignment.id}`,
            this.assignmentForm,
          );
          this.getAssignmentById();
          this.getAssignmentToDos();
          this.$toast.open({
            message: `${this.assignment.assignmentName} completed!`,
            color: 'green-500',
          });
        } catch (error) {
          this.$toast.open({ message: error.message, color: 'red-500' });
        }
      },

      async markAssignmentUncompleted() {
        this.assignmentForm.completed = false;
        this.assignmentForm.submitted = false;
        try {
          await axios.patch(
            `/api/user/assignments/${this.assignment.id}`,
            this.assignmentForm,
          );
          this.getAssignmentById();
          this.getAssignmentToDos();
          this.$toast.open({
            message: `${this.assignment.assignmentName} assignment is now incomplete!`,
            color: 'green-500',
          });
        } catch (error) {
          this.$toast.open({ message: error.message, color: 'red-500' });
        }
      },

      launchAssignmentFeedbackSideDrawer() {
        this.$sideDrawer.open({
          parent: this,
          component: FeedbackSideDrawer,
          props: {
            userId: this.user.id,
            entityId: Number(this.assignment.id),
            type: 'Assignment',
          },
          events: {
            getFeedback: () => this.getAssignmentFeedback(),
          },
        });
      },

      launchIndividualAssigneesSideDrawer() {
        this.$sideDrawer.open({
          parent: this,
          component: IndividualAssigneeSideDrawer,
          props: {
            assignmentTemplateId: this.assignment.assignmentTemplateId,
            userId: this.assignment.userId,
          },
        });
      },

      launchGroupAssigneesSideDrawer() {
        this.$sideDrawer.open({
          parent: this,
          component: GroupAssigneesSideDrawer,
          props: {
            groupName: this.assignment.assignmentTemplateGroupName,
            assignmentTemplateId: this.assignment.assignmentTemplateId,
          },
        });
      },
      toDoStatusIcon(todo) {
        if (todo.completed) {
          return 'check';
        }
        if (todo.submitted && !todo.completed) {
          return 'clock';
        }
        if (todo.toDoItemPreRequisiteComplete && !todo.submitted) {
          return 'chevron-right';
        }
        return 'lock';
      },

      toDoCanBeSubmitted(todo) {
        return todo.toDoItemPreRequisiteComplete && !todo.submitted;
      },

      toDoUnderReview(todo) {
        return todo.submitted && !todo.completed;
      },

    },
  };
</script>